export type LocationFaqItem = {
  question: string;
  answer: string;
};

/** Unique neighbourhoods / landmarks for location-page areaServed (caps at 10). */
export function uniqueServedAreaNames(
  locationName: string,
  ...groups: Array<string[] | undefined>
): string[] {
  const seen = new Set<string>([locationName.trim().toLowerCase()]);
  const out: string[] = [];
  for (const group of groups) {
    for (const raw of group ?? []) {
      const name = raw.trim();
      if (!name || seen.has(name.toLowerCase())) continue;
      seen.add(name.toLowerCase());
      out.push(name);
      if (out.length >= 10) return out;
    }
  }
  return out;
}

function firstDistinctPlace(
  locationName: string,
  ...groups: Array<string[] | undefined>
): string | undefined {
  const loc = locationName.trim().toLowerCase();
  for (const group of groups) {
    for (const raw of group ?? []) {
      const name = raw.trim();
      if (name && name.toLowerCase() !== loc) return name;
    }
  }
  return undefined;
}

export function defaultHyperLocalFaqExtras(options: {
  locationName: string;
  areasMention: string[];
  landmarks?: string[];
}): LocationFaqItem[] {
  const { locationName, areasMention, landmarks = [] } = options;
  const places = [...(landmarks ?? []), ...areasMention].filter((name) => name.trim());
  const landmark = firstDistinctPlace(locationName, landmarks, areasMention);
  const extras: LocationFaqItem[] = [
    {
      question: `Do you offer same-day house cleaning in ${locationName}?`,
      answer: `Yes. Book online before midday and we usually dispatch a ${locationName} crew the same day, subject to crew load. Instant confirmation is on the website.`,
    },
  ];
  if (landmark) {
    const route = [...new Set(places)].slice(0, 5).join(', ');
    extras.push({
      question: `Can you clean homes near ${landmark} in ${locationName}?`,
      answer: `Yes. Our ${locationName} route includes ${route}. We keep a single HQ address and serve ${locationName} as a service area — not a fake local storefront.`,
    });
  }
  return extras;
}

/** Append same-day + landmark FAQs when a location page does not already cover them. */
export function mergeHyperLocalFaqs(
  faq: LocationFaqItem[],
  options: { locationName: string; areasMention: string[]; landmarks?: string[] },
): LocationFaqItem[] {
  const extras = defaultHyperLocalFaqExtras(options);
  const existing = faq.map((item) => item.question.toLowerCase());
  const hasSameDay = existing.some(
    (question) => question.includes('same-day') || question.includes('same day'),
  );
  const landmark = firstDistinctPlace(options.locationName, options.landmarks, options.areasMention);
  const landmarkKey = landmark?.toLowerCase() ?? '';
  const hasLandmark = Boolean(landmarkKey) && existing.some((question) => question.includes(landmarkKey));
  const out = [...faq];

  for (const extra of extras) {
    const question = extra.question.toLowerCase();
    if (existing.includes(question)) continue;
    if ((question.includes('same-day') || question.includes('same day')) && hasSameDay) continue;
    if (question.includes('near ') && hasLandmark) continue;
    out.push(extra);
    existing.push(question);
  }
  return out;
}
